import React from 'react';
import { useTheme } from 'styled-components';
import { GapSizeType } from '../../../theme/type/gap-type';
import { HeadlineSizeType } from '../../../theme/type/headline-type';
import { PaletteColorType } from '../../../theme/type/palette-type';
import { cn } from '../../../utils/tailwind';
import { createStableClassToken } from '../../../utils/stable-class-token';

interface HeadlinePropsInterface {
    tag?: React.ElementType | keyof JSX.IntrinsicElements;
    level?: number | string;
    marginTop?: GapSizeType;
    gap?: GapSizeType;
    children: React.ReactNode;
    align?: 'left' | 'center' | 'right';
    inverse?: boolean;
    action?: React.ReactNode;
    variant?: PaletteColorType;
}

export const Headline: React.FC<HeadlinePropsInterface> = ({
    tag,
    level,
    marginTop = 'none',
    gap = 'medium',
    children,
    align = 'left',
    inverse,
    action,
    variant = 'secondary',
}) => {
    const theme = useTheme();
    const levelValue = level || 1;
    let levelKey = `h${levelValue}`;

    if (typeof levelValue === 'string') {
        levelKey = levelValue.startsWith('h') ? levelValue : `h${levelValue}`;
    }
    const normalizedLevel = levelKey as HeadlineSizeType;
    const Component = (tag || normalizedLevel) as React.ElementType;
    const textColor = inverse ? theme.palette.color.white.main : theme.palette.color[variant].main;
    const classToken = createStableClassToken('headline', [normalizedLevel, marginTop, gap, align, inverse, variant]);
    const hasAction = Boolean(action);

    const baseStyle: React.CSSProperties = {
        color: textColor,
        margin: `0 0 ${theme.gap[gap]}`,
        marginTop: theme.gap[marginTop],
        textAlign: align,
    };

    return (
        <>
            <style>{`
                .${classToken} a {
                    color: ${theme.palette.common.link.main};
                    text-decoration: underline;
                }

                .${classToken} a:hover {
                    color: ${theme.palette.common.link.hover};
                }

                .${classToken} > span {
                    color: ${theme.palette.color.primary.main};
                }

                .${classToken} {
                    font-size: ${theme.headline.size[normalizedLevel].mobile.fontSize};
                    line-height: ${theme.headline.size[normalizedLevel].mobile.lineHeight};
                }

                @media ${theme.breakpoints.md.min} {
                    .${classToken} {
                        font-size: ${theme.headline.size[normalizedLevel].fontSize};
                        line-height: ${theme.headline.size[normalizedLevel].lineHeight};
                    }
                }
            `}</style>
            <Component
                className={cn(classToken, 'font-bold', hasAction && 'flex items-center justify-between')}
                style={baseStyle}
            >
                {children}
                {hasAction && <div>{action}</div>}
            </Component>
        </>
    );
};
